import type { MemoDraft } from '@cyberscore/contracts';
import { Copy, FileText, Loader2 } from 'lucide-react';
import { useToast } from '../../ui/Toasts';
import { assistantError, useDraftMemo } from './api';

/** The memo as plain text, to paste into a decision or the policy system. */
export function memoText(m: MemoDraft, business: string) {
  const lines = [`Underwriting memo: ${business} (CyberScore ${m.score})`, '', m.summary];
  if (m.strengths.length) lines.push('', 'Strengths', ...m.strengths.map((s) => `- ${s}`));
  if (m.concerns.length) lines.push('', 'Concerns', ...m.concerns.map((s) => `- ${s}`));
  if (m.conditions.length) lines.push('', 'Suggested conditions', ...m.conditions.map((c) => `- ${c.title}${c.control ? ` [${c.control}]` : ''}${c.reason ? `: ${c.reason}` : ''}`)); // prettier-ignore
  lines.push(
    '',
    'Draft by the CyberScore assistant from the assessment’s facts. Check it before you rely on it.',
  );
  return lines.join('\n');
}

/**
 * An underwriting memo draft for one assessment (task 74), asked for by the underwriter. Drafted from the
 * score's facts; every control it names is one the assessment has. Nothing is saved.
 */
export function MemoCard({ id, business }: { id: string; business: string }) {
  const draft = useDraftMemo(id);
  const toast = useToast();
  const m = draft.data;
  const copy = () =>
    navigator.clipboard
      .writeText(memoText(m!, business))
      .then(() => toast('Memo copied'))
      .catch(() => toast('Could not copy. Select the text instead.', 'bad'));
  return (
    <section className="card pad memo-card" aria-labelledby="memo-title">
      <h2 id="memo-title" className="section-title"><FileText size={16} aria-hidden /> Underwriting memo</h2>
      {!m && <p className="muted small">A draft from this score’s facts: the cap, the evidence grade, open conditions, conflicts and the weakest protections. Nothing is saved.</p>}
      {draft.isError && <div className="result bad" role="alert">{assistantError(draft.error)}</div>}
      {m && (
        <div className="memo" role="status">
          {m.how === 'template' && <p className="muted small">The model’s draft didn’t match the facts{m.problems.length ? ` (${m.problems.slice(0, 2).join('; ')})` : ''}, so this is a plain memo from the facts.</p>}
          <p>{m.summary}</p>
          {m.strengths.length > 0 && <><h3>Strengths</h3><ul>{m.strengths.map((s) => <li key={s}>{s}</li>)}</ul></>}
          {m.concerns.length > 0 && <><h3>Concerns</h3><ul>{m.concerns.map((s) => <li key={s}>{s}</li>)}</ul></>}
          {m.conditions.length > 0 && <><h3>Suggested conditions</h3><ul>{m.conditions.map((c) => <li key={c.title}>{c.title}{c.control && <span className="mono muted"> [{c.control}]</span>}{c.reason && <span className="muted">: {c.reason}</span>}</li>)}</ul></>}
          <p className="muted tiny">Draft by the assistant ({m.model}). Check it before you rely on it.</p>
        </div>
      )}
      <div className="row gap mt-s">
        <button type="button" className="btn btn-sm" disabled={draft.isPending} onClick={() => draft.mutate()}>{draft.isPending && <Loader2 size={14} className="spin" />}{m ? 'Draft again' : 'Draft a memo'}</button>
        {m && <button type="button" className="btn btn-sm btn-ghost" onClick={copy}><Copy size={14} /> Copy</button>}
      </div>
    </section>
  ); // prettier-ignore
}
